import React, { useMemo, useRef, useCallback } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import styles from './QuillEditor.module.css';

/**
 * Reusable Quill Text Editor Component (React 19 compatible)
 *
 * Built on top of `react-quill-new` — a maintained fork of react-quill that
 * works with React 18/19. External logic (API calls, validations, form handling)
 * should be handled by the parent.
 *
 * Props:
 * @param {string}   value          - Controlled HTML content of the editor.
 * @param {function} onChange       - Callback invoked with the updated HTML string.
 * @param {string}   placeholder    - Placeholder text shown when editor is empty.
 * @param {boolean}  readOnly       - Disables editing when true.
 * @param {string}   className      - Optional class for the outer wrapper.
 * @param {object}   style          - Optional inline styles for the outer wrapper.
 * @param {number}   minHeight      - Minimum editor height in pixels (default 300).
 * @param {number}   maxHeight      - Maximum editor height in pixels (default 600).
 * @param {boolean}  showWordCount  - Whether to show the live word/char counter (default true).
 * @param {object}   modules        - Override default Quill modules.
 * @param {array}    formats        - Override default allowed formats.
 * @param {string}   theme          - Quill theme, defaults to 'snow'.
 */
const QuillEditor = ({
  value = '',
  onChange,
  placeholder = 'Start writing...',
  readOnly = false,
  className = '',
  style = {},
  minHeight = 300,
  maxHeight = 600,
  showWordCount = true,
  modules: customModules,
  formats: customFormats,
  theme = 'snow',
}) => {
  const quillRef = useRef(null);

  /* ---------------------------------------------------------------------- */
  /*  Toolbar configuration — full feature set                              */
  /* ---------------------------------------------------------------------- */
  const defaultModules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ font: [] }, { size: ['small', false, 'large', 'huge'] }],
          [{ header: [1, 2, 3, 4, 5, 6, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ script: 'sub' }, { script: 'super' }],
          [{ list: 'ordered' }, { list: 'bullet' }, { list: 'check' }],
          [{ indent: '-1' }, { indent: '+1' }],
          [{ align: [] }, { direction: 'rtl' }],
          ['blockquote', 'code-block'],
          ['link', 'image', 'video', 'formula'],
          ['clean'],
        ],
      },
      clipboard: {
        matchVisual: false,
      },
      history: {
        delay: 1000,
        maxStack: 100,
        userOnly: true,
      },
    }),
    []
  );

  const defaultFormats = useMemo(
    () => [
      'font',
      'size',
      'header',
      'bold',
      'italic',
      'underline',
      'strike',
      'color',
      'background',
      'script',
      'list',
      'bullet',
      'indent',
      'align',
      'direction',
      'blockquote',
      'code-block',
      'link',
      'image',
      'video',
      'formula',
    ],
    []
  );

  const modules = customModules || defaultModules;
  const formats = customFormats || defaultFormats;

  /* ---------------------------------------------------------------------- */
  /*  Change handler                                                        */
  /* ---------------------------------------------------------------------- */
  const handleChange = useCallback(
    (content, delta, source, editor) => {
      if (typeof onChange === 'function') {
        onChange(content, { delta, source, editor });
      }
    },
    [onChange]
  );

  /* ---------------------------------------------------------------------- */
  /*  Word & character count (derived from plain text)                      */
  /* ---------------------------------------------------------------------- */
  const { wordCount, charCount } = useMemo(() => {
    const plain = (value || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/&[a-z]+;/gi, ' ')
      .trim();

    const words = plain.length === 0 ? 0 : plain.split(/\s+/).filter(Boolean).length;
    const chars = plain.length;
    return { wordCount: words, charCount: chars };
  }, [value]);

  /* ---------------------------------------------------------------------- */
  /*  Render                                                                */
  /* ---------------------------------------------------------------------- */
  const wrapperStyle = {
    '--qe-min-height': `${minHeight}px`,
    '--qe-max-height': `${maxHeight}px`,
    ...style,
  };

  return (
    <div
      className={`${styles.wrapper} ${readOnly ? styles.readOnly : ''} ${className}`}
      style={wrapperStyle}
    >
      <div className={styles.editorContainer}>
        <ReactQuill
          ref={quillRef}
          theme={theme}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          readOnly={readOnly}
          modules={modules}
          formats={formats}
        />
      </div>

      {showWordCount && (
        <div className={styles.footer}>
          <span className={styles.counter}>
            <strong>{wordCount}</strong> {wordCount === 1 ? 'word' : 'words'}
          </span>
          <span className={styles.divider}>•</span>
          <span className={styles.counter}>
            <strong>{charCount}</strong> {charCount === 1 ? 'character' : 'characters'}
          </span>
        </div>
      )}
    </div>
  );
};

export default QuillEditor;